// What the status strip knows, read from the Engine mailbox list (GET /api/mailboxes).
// One request is shared by every reader for 60 seconds. A failed read is Unknown, never Off.
import { createEngineMailboxClient } from './engine-mailbox-client.js'

export const STATUS_STALE_MS = 60000
export const UNKNOWN_STATUS = Object.freeze({ sending: 'unknown', attention: null })

const NEEDS_ATTENTION_CONNECTION = new Set(['held', 'stale'])

export function mailboxNeedsAttention(row) {
  return NEEDS_ATTENTION_CONNECTION.has(row?.connection?.status) || row?.policy?.paused === true || row?.policy?.quarantined === true || row?.policy?.suppressed === true
}

// sending is only On or Off when the server said so with a true or false.
export function summarizeMailboxList(list) {
  const sending = list?.sendingEnabled === true ? 'on' : list?.sendingEnabled === false ? 'off' : 'unknown'
  const rows = Array.isArray(list?.mailboxes) ? list.mailboxes : null
  return { sending, attention: rows ? rows.filter(mailboxNeedsAttention).length : null }
}

// `load` returns the mailbox list. The store keeps one answer for staleMs and one request in flight.
export function createShellStatusStore({ load, staleMs = STATUS_STALE_MS, now = Date.now } = {}) {
  let cached = null
  let inFlight = null
  return {
    read() {
      if (cached && now() - cached.at < staleMs) return Promise.resolve(cached.value)
      if (!inFlight) {
        inFlight = Promise.resolve().then(load).then(
          list => { const value = summarizeMailboxList(list); cached = { at: now(), value }; return value },
          () => UNKNOWN_STATUS,
        ).finally(() => { inFlight = null })
      }
      return inFlight
    },
    reset() { cached = null },
  }
}

// One store per signed-in session and selected workspace, so neither a different person nor a switched workspace sees the previous answer.
const stores = new Map()
export function shellStatusStoreFor({ baseUrl, getToken, userId, sessionId, allowLocal = false, workspaceId = null }) {
  const key = `${baseUrl}|${userId}|${sessionId}|${workspaceId ?? ''}`
  if (!stores.has(key)) {
    if (stores.size > 4) stores.clear()
    let client = null
    try {
      client = createEngineMailboxClient({ baseUrl, getToken, allowLocal, expectedUserId: userId, expectedSessionId: sessionId, workspaceId })
    } catch { /* not configured: every read is Unknown */ }
    stores.set(key, createShellStatusStore({ load: () => (client ? client.list() : Promise.reject(new Error('not configured'))) }))
  }
  return stores.get(key)
}

// Plain-English pieces of the strip. A viewer sees only the last reply slot (no mailbox health).
// Last reply reads Not connected yet until the held reply list has a screen.
export function buildStripModel({ role, status = UNKNOWN_STATUS, machineRoomUrl = null, loading = false }) {
  const lastReply = { key: 'last-reply', label: 'Last reply: Not connected yet', tone: 'neutral' }
  if (role === 'viewer') return [lastReply]
  const sending = status.sending
  const items = [
    { key: 'sending', label: loading ? 'Sending: Checking' : `Sending: ${sending === 'on' ? 'On' : sending === 'off' ? 'Off' : 'Unknown'}`, tone: loading || sending === 'unknown' ? 'warning' : sending === 'on' ? 'success' : 'error' },
    lastReply,
  ]
  if (loading) items.push({ key: 'mailboxes', label: 'Mailboxes: Checking', tone: 'neutral' })
  else if (status.attention === null) items.push({ key: 'mailboxes', label: 'Mailboxes: Unknown', tone: 'warning' })
  else if (status.attention === 0) items.push({ key: 'mailboxes', label: 'No mailboxes need attention', tone: 'success' })
  else items.push({ key: 'mailboxes', label: `${status.attention} ${status.attention === 1 ? 'mailbox needs' : 'mailboxes need'} attention`, tone: 'warning' })
  if (machineRoomUrl) items.push({ key: 'machine-room', label: 'Machine Room', tone: 'link', href: machineRoomUrl })
  return items
}
